import { jsPDF } from 'jspdf';
import robotoBoldUrl from '@/assets/fonts/Roboto-Bold.ttf?url';
import robotoRegularUrl from '@/assets/fonts/Roboto-Regular.ttf?url';
import {
  BOOK_ICON_PATHS,
  BOOK_ICON_STROKE_WIDTH,
  SHUFFLE_ICON_PATHS,
} from '@/components/Icons/paths';
import {
  EMPTY_LESSON_SUBJECT,
  type DiaryEntry,
  type DiaryPrintOptions,
} from '@/types/diary';
import { formatDate, formatDiaryDate } from '@/utils/date';
import { getHomeworkTexts, getTopicTexts } from '@/utils/diary';
import { drawPdfIcon } from '@/utils/pdfIcons';
import { joinNonEmpty, stripHtml } from '@/utils/text';

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const PAGE_MARGIN = 20;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const FOOTER_BASELINE = PAGE_HEIGHT - PAGE_MARGIN;

const SUBJECT_COLUMN_WIDTH = 190;
const LESSON_NUMBER_WIDTH = 12;
const CELL_PADDING = 4;
const SECTION_SPACING = 10;
const MIN_ROW_HEIGHT = 20;
const LINE_HEIGHT = 1.2;
const ICON_SIZE = 8;

const NAME_FONT_SIZE = 11;
const DATE_FONT_SIZE = 10;
const CELL_FONT_SIZE = 9;
const TINY_FONT_SIZE = 7;
const FOOTER_SPACING = 8;

const BORDER_COLOR = '#dddfe6';
const HEADER_BACKGROUND_COLOR = '#f2f3f6';
const TEXT_COLOR = '#000000';
const MUTED_TEXT_COLOR = '#757575';
const CHANGED_LESSON_COLOR = '#fd7c34';

const CONTENT_BOTTOM =
  FOOTER_BASELINE - TINY_FONT_SIZE * LINE_HEIGHT - FOOTER_SPACING;

const FONT_FAMILY = 'Roboto';
const REGULAR = 'normal';
const BOLD = 'bold';

const toBase64 = (buffer: ArrayBuffer): string => {
  const CHUNK_SIZE = 0x8000;
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let index = 0; index < bytes.length; index += CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK_SIZE));
  }

  return btoa(binary);
};

const fetchFont = (url: string): Promise<string> =>
  fetch(url)
    .then((response) => response.arrayBuffer())
    .then(toBase64);

let fontsPromise: Promise<[string, string]> | undefined;

const registerFonts = async (doc: jsPDF): Promise<void> => {
  fontsPromise ??= Promise.all([
    fetchFont(robotoRegularUrl),
    fetchFont(robotoBoldUrl),
  ]);

  const [regular, bold] = await fontsPromise;

  doc.addFileToVFS('Roboto-Regular.ttf', regular);
  doc.addFont('Roboto-Regular.ttf', FONT_FAMILY, REGULAR);
  doc.addFileToVFS('Roboto-Bold.ttf', bold);
  doc.addFont('Roboto-Bold.ttf', FONT_FAMILY, BOLD);
};

const getColumnWidths = ({
  isTopicColumnVisible,
  isHomeworkColumnVisible,
}: DiaryPrintOptions): number[] => {
  const textColumnCount =
    Number(isTopicColumnVisible) + Number(isHomeworkColumnVisible);

  if (!textColumnCount) {
    return [CONTENT_WIDTH];
  }

  const textColumnWidth =
    (CONTENT_WIDTH - SUBJECT_COLUMN_WIDTH) / textColumnCount;

  return [
    SUBJECT_COLUMN_WIDTH,
    ...Array.from({ length: textColumnCount }, () => textColumnWidth),
  ];
};

export interface DiaryPdfLabels {
  subject: string;
  topic: string;
  homework: string;
  emptyLesson: string;
  emptyDescription: string;
  deletedHomeTask: string;
  teacherMessage: string;
  behaviourEntry: string;
  prepared: string;
}

interface DiaryPdfParams {
  fileName: string;
  studentName: string;
  entries: DiaryEntry[];
  options: DiaryPrintOptions;
  labels: DiaryPdfLabels;
}

export const downloadDiaryPdf = async ({
  fileName,
  studentName,
  entries,
  options,
  labels,
}: DiaryPdfParams): Promise<void> => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  await registerFonts(doc);

  const { isTopicColumnVisible, isHomeworkColumnVisible } = options;
  const columnWidths = getColumnWidths(options);
  const textColumnWidths = columnWidths.slice(1);
  const days = entries.filter((entry) => entry.lessons.length > 0);
  let y = PAGE_MARGIN;

  const setFont = (fontStyle: string, fontSize: number, color = TEXT_COLOR) => {
    doc.setFont(FONT_FAMILY, fontStyle);
    doc.setFontSize(fontSize);
    doc.setTextColor(color);
  };

  const splitText = (
    text: string,
    width: number,
    fontStyle: string,
    fontSize: number,
  ): string[] => {
    setFont(fontStyle, fontSize);

    return text ? doc.splitTextToSize(text, width) : [];
  };

  const getTextHeight = (lineCount: number, fontSize: number) =>
    lineCount * fontSize * LINE_HEIGHT + CELL_PADDING * 2;

  const addPage = () => {
    doc.addPage();
    y = PAGE_MARGIN;
  };

  const ensureSpace = (height: number) => {
    if (y + height > CONTENT_BOTTOM) {
      addPage();
    }
  };

  const drawLines = (
    lines: string[],
    x: number,
    fontStyle: string,
    fontSize: number,
    color = TEXT_COLOR,
  ) => {
    setFont(fontStyle, fontSize, color);
    lines.forEach((line, index) => {
      doc.text(
        line,
        x,
        y + CELL_PADDING + (index + 0.85) * fontSize * LINE_HEIGHT,
      );
    });
  };

  const drawRowBorders = (height: number, hasColumnSeparators: boolean) => {
    doc.setDrawColor(BORDER_COLOR);
    doc.setLineWidth(1);
    doc.rect(PAGE_MARGIN, y, CONTENT_WIDTH, height);

    if (!hasColumnSeparators) {
      return;
    }

    let x = PAGE_MARGIN;

    columnWidths.slice(0, -1).forEach((width) => {
      x += width;
      doc.line(x, y, x, y + height);
    });
  };

  const drawHeaderRow = () => {
    const headers = [
      labels.subject,
      isTopicColumnVisible && labels.topic,
      isHomeworkColumnVisible && labels.homework,
    ].filter((header): header is string => !!header);
    const height = getTextHeight(1, CELL_FONT_SIZE);

    ensureSpace(height);
    doc.setFillColor(HEADER_BACKGROUND_COLOR);
    doc.rect(PAGE_MARGIN, y, CONTENT_WIDTH, height, 'F');
    drawRowBorders(height, true);

    let x = PAGE_MARGIN;

    headers.forEach((header, index) => {
      drawLines([header], x + CELL_PADDING, REGULAR, CELL_FONT_SIZE);
      x += columnWidths[index] ?? 0;
    });

    y += height;
  };

  const drawDateRow = (date: string) => {
    const height = getTextHeight(1, DATE_FONT_SIZE);

    ensureSpace(height);
    drawRowBorders(height, false);
    drawLines(
      [formatDiaryDate(date)],
      PAGE_MARGIN + CELL_PADDING,
      BOLD,
      DATE_FONT_SIZE,
    );
    y += height;
  };

  const drawLessonRow = (lesson: DiaryEntry['lessons'][number]) => {
    const isEmptyLesson = lesson.className === EMPTY_LESSON_SUBJECT;
    const subject = isEmptyLesson ? labels.emptyLesson : lesson.className;
    const room = lesson.roomNumber ?? '';

    setFont(REGULAR, CELL_FONT_SIZE);
    const roomWidth = room ? doc.getTextWidth(room) + CELL_PADDING * 2 : 0;
    const markerWidth = lesson.isOnlyForCurrentWeek
      ? ICON_SIZE + CELL_PADDING
      : 0;
    const subjectWidth =
      SUBJECT_COLUMN_WIDTH -
      CELL_PADDING * 2 -
      LESSON_NUMBER_WIDTH -
      CELL_PADDING -
      roomWidth -
      markerWidth;

    const subjectLines = splitText(subject, subjectWidth, BOLD, CELL_FONT_SIZE);
    const textCells = [
      isTopicColumnVisible && getTopicTexts(lesson).join('\n\n'),
      isHomeworkColumnVisible &&
        getHomeworkTexts({
          lesson,
          emptyLabel: labels.emptyDescription,
          deletedLabel: labels.deletedHomeTask,
        }).join('\n\n'),
    ].filter((text): text is string => text !== false);
    const textCellLines = textCells.map((text, index) =>
      splitText(
        text,
        (textColumnWidths[index] ?? CONTENT_WIDTH) - CELL_PADDING * 2,
        REGULAR,
        CELL_FONT_SIZE,
      ),
    );
    const lineCount = Math.max(
      subjectLines.length,
      ...textCellLines.map((lines) => lines.length),
      1,
    );
    const height = Math.max(
      getTextHeight(lineCount, CELL_FONT_SIZE),
      MIN_ROW_HEIGHT,
    );

    ensureSpace(height);
    drawRowBorders(height, true);

    const iconY =
      y + CELL_PADDING + (CELL_FONT_SIZE * LINE_HEIGHT - ICON_SIZE) / 2;

    if (lesson.lessonNumber === undefined) {
      drawPdfIcon({
        doc,
        paths: BOOK_ICON_PATHS,
        x: PAGE_MARGIN + CELL_PADDING + (LESSON_NUMBER_WIDTH - ICON_SIZE) / 2,
        y: iconY,
        size: ICON_SIZE,
        color: MUTED_TEXT_COLOR,
        strokeWidth: BOOK_ICON_STROKE_WIDTH,
      });
    } else {
      drawLines(
        [String(lesson.lessonNumber)],
        PAGE_MARGIN + CELL_PADDING,
        REGULAR,
        TINY_FONT_SIZE,
        MUTED_TEXT_COLOR,
      );
    }

    const subjectX = PAGE_MARGIN + CELL_PADDING + LESSON_NUMBER_WIDTH;

    drawLines(subjectLines, subjectX, BOLD, CELL_FONT_SIZE);

    if (room) {
      setFont(REGULAR, CELL_FONT_SIZE, MUTED_TEXT_COLOR);
      doc.text(
        room,
        PAGE_MARGIN + SUBJECT_COLUMN_WIDTH - CELL_PADDING - markerWidth,
        y + CELL_PADDING + 0.85 * CELL_FONT_SIZE * LINE_HEIGHT,
        { align: 'right' },
      );
    }

    if (lesson.isOnlyForCurrentWeek) {
      drawPdfIcon({
        doc,
        paths: SHUFFLE_ICON_PATHS,
        x: PAGE_MARGIN + SUBJECT_COLUMN_WIDTH - CELL_PADDING - ICON_SIZE,
        y: iconY,
        size: ICON_SIZE,
        color: CHANGED_LESSON_COLOR,
      });
    }

    let x = PAGE_MARGIN + SUBJECT_COLUMN_WIDTH;

    textCellLines.forEach((lines, index) => {
      drawLines(lines, x + CELL_PADDING, REGULAR, CELL_FONT_SIZE);
      x += textColumnWidths[index] ?? 0;
    });

    y += height;
  };

  const drawNotesSection = (title: string, texts: string[]) => {
    if (!texts.length) {
      return;
    }

    const titleHeight = CELL_FONT_SIZE * LINE_HEIGHT;

    ensureSpace(titleHeight);
    setFont(BOLD, CELL_FONT_SIZE);
    doc.text(title, PAGE_MARGIN, y + CELL_FONT_SIZE);
    y += titleHeight;

    texts.forEach((text) => {
      const lines = splitText(text, CONTENT_WIDTH, REGULAR, CELL_FONT_SIZE);
      const height = lines.length * CELL_FONT_SIZE * LINE_HEIGHT;

      ensureSpace(height);
      setFont(REGULAR, CELL_FONT_SIZE);
      lines.forEach((line, index) => {
        doc.text(
          line,
          PAGE_MARGIN,
          y + (index + 1) * CELL_FONT_SIZE * LINE_HEIGHT,
        );
      });
      y += height;
    });
  };

  setFont(BOLD, NAME_FONT_SIZE);
  doc.text(studentName, PAGE_MARGIN, y + NAME_FONT_SIZE);
  y += NAME_FONT_SIZE * LINE_HEIGHT + CELL_PADDING;

  days.forEach((entry, index) => {
    if (index === 0) {
      drawHeaderRow();
    }

    drawDateRow(entry.date);
    entry.lessons.forEach(drawLessonRow);

    drawNotesSection(
      labels.teacherMessage,
      entry.notes.map((note) =>
        joinNonEmpty([
          stripHtml(note.text),
          note.authorName,
          formatDate(note.timeCreated),
        ]),
      ),
    );
    drawNotesSection(
      labels.behaviourEntry,
      entry.behaviourEntries.map((behaviourEntry) =>
        joinNonEmpty([
          stripHtml(behaviourEntry.text),
          behaviourEntry.subject,
          formatDate(behaviourEntry.timeCreated),
        ]),
      ),
    );

    y += SECTION_SPACING;
  });

  setFont(REGULAR, TINY_FONT_SIZE);
  doc.text(labels.prepared, PAGE_WIDTH - PAGE_MARGIN, FOOTER_BASELINE, {
    align: 'right',
  });

  doc.save(fileName);
};
